/**
 * Custom delimiter code parsing utilities
 * Provides reliable code block detection for AI responses using custom delimiters
 */

export interface CodeBlock {
  id: string;
  language: string;
  content: string;
  startIndex: number;
  endIndex: number;
  isComplete: boolean;
}

export interface ParsedContent {
  processedContent: string;
  codeBlocks: CodeBlock[];
  hasIncompleteBlocks: boolean;
}

// Custom delimiter patterns
const CODE_START_DELIMITER = '<<<GIATEC_CODE_START';
const CODE_END_DELIMITER = '<<<GIATEC_CODE_END>>>';

// Regex patterns for parsing
const COMPLETE_CODE_BLOCK_REGEX = /<<<GIATEC_CODE_START(?::(\w+))?>>>([\s\S]*?)<<<GIATEC_CODE_END>>>/g;
const START_DELIMITER_REGEX = /<<<GIATEC_CODE_START(?::(\w+))?>>>/;
const END_DELIMITER_REGEX = /<<<GIATEC_CODE_END>>>/;

/**
 * Extract all complete custom code blocks from content
 */
export function extractCustomCodeBlocks(content: string): CodeBlock[] {
  const codeBlocks: CodeBlock[] = [];
  let match;
  let blockId = 0;

  // Reset regex state
  COMPLETE_CODE_BLOCK_REGEX.lastIndex = 0;

  while ((match = COMPLETE_CODE_BLOCK_REGEX.exec(content)) !== null) {
    const [fullMatch, language, code] = match;
    
    codeBlocks.push({
      id: `custom-code-${blockId++}`,
      language: language || 'text',
      content: code.trim(),
      startIndex: match.index,
      endIndex: match.index + fullMatch.length,
      isComplete: true
    });
  }

  return codeBlocks;
}

/**
 * Check for incomplete code blocks (start delimiter without end)
 */
export function findIncompleteCodeBlocks(content: string): CodeBlock[] {
  const incompleteBlocks: CodeBlock[] = [];
  const startMatches: RegExpExecArray[] = [];
  let match;

  // Find all start delimiters
  const startRegex = new RegExp(START_DELIMITER_REGEX.source, 'g');
  while ((match = startRegex.exec(content)) !== null) {
    startMatches.push(match);
  }

  // Find all end delimiters
  const endMatches: RegExpExecArray[] = [];
  const endRegex = new RegExp(END_DELIMITER_REGEX.source, 'g');
  while ((match = endRegex.exec(content)) !== null) {
    endMatches.push(match);
  }

  // Check for unmatched start delimiters
  let endIndex = 0;
  let blockId = 0;

  for (const startMatch of startMatches) {
    const startPos = startMatch.index;
    
    // Find the next end delimiter after this start
    let foundEnd = false;
    while (endIndex < endMatches.length) {
      const endMatch = endMatches[endIndex];
      if (endMatch.index > startPos) {
        foundEnd = true;
        endIndex++;
        break;
      }
      endIndex++;
    }

    // If no matching end found, this is an incomplete block
    if (!foundEnd) {
      const language = startMatch[1] || 'text';
      const codeStartIndex = startMatch.index + startMatch[0].length;
      const codeContent = content.substring(codeStartIndex);

      incompleteBlocks.push({
        id: `incomplete-code-${blockId++}`,
        language,
        content: codeContent,
        startIndex: startMatch.index,
        endIndex: content.length,
        isComplete: false
      });
    }
  }

  return incompleteBlocks;
}

/**
 * Remove custom delimiters from content, replacing with markdown code blocks
 */
export function convertCustomToMarkdown(content: string): string {
  return content.replace(
    COMPLETE_CODE_BLOCK_REGEX,
    (match, language, code) => {
      const lang = language || '';
      return `\`\`\`${lang}\n${code.trim()}\n\`\`\``;
    }
  );
}

/**
 * Process content with dual detection system
 */
export function parseCodeBlocks(content: string): ParsedContent {
  // Extract complete custom code blocks
  const customCodeBlocks = extractCustomCodeBlocks(content);
  
  // Find incomplete blocks for streaming scenarios
  const incompleteBlocks = findIncompleteCodeBlocks(content);
  
  // Process content: convert custom delimiters to markdown for standard rendering
  let processedContent = convertCustomToMarkdown(content);
  
  // Remove incomplete blocks from processed content (they'll be handled separately)
  for (const block of incompleteBlocks) {
    const delimiterPattern = `${CODE_START_DELIMITER}${block.language !== 'text' ? ':' + block.language : ''}>>>`;
    const blockPattern = delimiterPattern + block.content;
    processedContent = processedContent.replace(blockPattern, '');
  }

  return {
    processedContent,
    codeBlocks: [...customCodeBlocks, ...incompleteBlocks],
    hasIncompleteBlocks: incompleteBlocks.length > 0
  };
}

/**
 * Extract all code blocks (both custom and markdown) from content
 */
export function extractAllCodeBlocks(content: string): CodeBlock[] {
  const allBlocks: CodeBlock[] = [];
  let blockId = 0;

  // First, extract custom delimited code blocks
  const customBlocks = extractCustomCodeBlocks(content);
  allBlocks.push(...customBlocks);

  // Then extract standard markdown code blocks from remaining content
  let remainingContent = content;
  
  // Remove custom blocks from content to avoid double-processing
  for (const block of customBlocks) {
    const startPos = block.startIndex;
    const endPos = block.endIndex;
    remainingContent = remainingContent.slice(0, startPos) + 
      ' '.repeat(endPos - startPos) + 
      remainingContent.slice(endPos);
  }

  // Extract markdown code blocks
  const markdownCodeRegex = /```(\w+)?\n?([\s\S]*?)\n?```/g;
  let markdownMatch;

  while ((markdownMatch = markdownCodeRegex.exec(remainingContent)) !== null) {
    const [fullMatch, language, code] = markdownMatch;
    
    allBlocks.push({
      id: `markdown-code-${blockId++}`,
      language: language || 'text',
      content: code.trim(),
      startIndex: markdownMatch.index,
      endIndex: markdownMatch.index + fullMatch.length,
      isComplete: true
    });
  }

  // Sort by start index to maintain order
  return allBlocks.sort((a, b) => a.startIndex - b.startIndex);
}

/**
 * Validate if content has properly formatted code blocks
 */
export function validateCodeFormatting(content: string): {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
} {
  const issues: string[] = [];
  const suggestions: string[] = [];
  
  // Check for unmatched delimiters
  const startCount = (content.match(new RegExp(START_DELIMITER_REGEX.source, 'g')) || []).length;
  const endCount = (content.match(new RegExp(END_DELIMITER_REGEX.source, 'g')) || []).length;
  
  if (startCount !== endCount) {
    issues.push(`Unmatched code delimiters: ${startCount} start, ${endCount} end`);
    suggestions.push('Ensure every <<<GIATEC_CODE_START>>> has a matching <<<GIATEC_CODE_END>>>');
  }

  // Check for nested delimiters
  const incompleteBlocks = findIncompleteCodeBlocks(content);
  if (incompleteBlocks.length > 0) {
    issues.push(`Found ${incompleteBlocks.length} incomplete code blocks`);
    suggestions.push('Complete all code blocks before processing');
  }

  return {
    isValid: issues.length === 0,
    issues,
    suggestions
  };
}

/**
 * Get language from code content heuristically
 */
export function detectLanguage(code: string): string {
  const trimmedCode = code.trim().toLowerCase();
  
  // Common patterns for language detection
  if (trimmedCode.includes('function') && trimmedCode.includes('{')) {
    return 'javascript';
  }
  if (trimmedCode.includes('def ') || trimmedCode.includes('import ')) {
    return 'python';
  }
  if (trimmedCode.includes('SELECT') || trimmedCode.includes('select')) {
    return 'sql';
  }
  if (trimmedCode.includes('<') && trimmedCode.includes('>')) {
    return 'html';
  }
  if (trimmedCode.includes('class ') && trimmedCode.includes('{')) {
    return 'java';
  }
  if (trimmedCode.includes('#include') || trimmedCode.includes('int main')) {
    return 'cpp';
  }
  if (trimmedCode.includes('#!/bin/bash') || trimmedCode.includes('#!/bin/sh')) {
    return 'bash';
  }
  
  return 'text';
}